import { DL_REF, DL_VIEW_CHAT, DL_VIEW_NOTES, contactMessagesRoute, followupsCaseRoute } from '@/utils/deepLinks'

/**
 * The framework-free logic the four inbox pages share, pulled out of
 * `useInboxPage` so it's unit-tested without mounting a page: where a card's
 * click lands, the attended-by (assignee) filter + its counts, and the little
 * sum/plural helpers. No refs, no router, no Apollo — call these from computeds.
 */

/**
 * WHERE a staff member sits, carried on their link (Sandy 2026-08-25): the four
 * filters that can hide somebody on the follow-ups page — company, lead agency,
 * role and lifecycle status. Only the present ones are emitted.
 */
export function staffScopeQuery(card) {
  return {
    ...(card.companyId != null ? { cid: String(card.companyId) } : {}),
    ...(card.agencyId != null ? { aid: String(card.agencyId) } : {}),
    ...(card.category ? { cat: String(card.category) } : {}),
    ...(card.status ? { status: String(card.status) } : {}),
  }
}

/**
 * Where a card's click lands, per FACE ('chat' | 'notes'). The face travels
 * EXPLICITLY both ways so the destination never guesses from an absent param;
 * `ref: inbox` makes the arrival treat it as a foreign deep link (all toolbar
 * filters reset, the entity always visible).
 */
export function routeFor(card, face) {
  if (card.kind === 'case') {
    return followupsCaseRoute({
      companyId: card.companyId,
      agencyId: card.agencyId,
      caseId: card.entityId,
      ref: DL_REF.inbox,
      view: face === 'chat' ? DL_VIEW_CHAT : DL_VIEW_NOTES,
    })
  }

  if (card.kind === 'staff') {
    return {
      name: 'staffing-followups',
      query: {
        staffId: String(card.entityId),
        ...staffScopeQuery(card),
        ...(face === 'chat' ? { chat: '1' } : { notes: '1' }),
      },
    }
  }

  return contactMessagesRoute({
    contactId: card.entityId,
    chat: face === 'chat',
    notes: face !== 'chat',
  })
}

/**
 * The distinct assignees across the rows — named + coloured, sorted by label.
 * Only case rows carry the person's name/colour; a line-owned row has just the
 * id, so whichever row DOES know fills the gap. A nameless one falls back to
 * "User #<id>".
 */
export function buildAssignees(rows) {
  const byId = new Map()

  for (const row of rows ?? []) {
    if (row.assignedToId == null) continue

    const key = String(row.assignedToId)
    const known = byId.get(key)

    if (known) {
      known.label = known.label || row.assignedToLabel || null
      known.color = known.color || row.assignedToColor || null
      continue
    }

    byId.set(key, {
      id: key,
      label: row.assignedToLabel || null,
      color: row.assignedToColor || null,
    })
  }

  return [...byId.values()]
    .map(u => ({ ...u, label: u.label || `User #${u.id}` }))
    .sort((a, b) => (a.label || '').localeCompare(b.label || ''))
}

/** How many rows each assignee owns, keyed by assignee id (string). */
export function buildAssigneeCounts(rows) {
  const counts = {}

  for (const row of rows ?? []) {
    if (row.assignedToId == null) continue

    const key = String(row.assignedToId)

    counts[key] = (counts[key] ?? 0) + 1
  }

  return counts
}

/**
 * Filter a list by the selected assignee: `null` = everybody, the sentinel
 * (`'unknown'`) = only what belongs to NOBODY, otherwise a specific id.
 */
export function forAssignee(list, ownerOf, selectedId, unknownOwner = 'unknown') {
  if (selectedId == null) return list

  if (selectedId === unknownOwner) return list.filter(r => ownerOf(r) == null)

  return list.filter(r => String(ownerOf(r) ?? '') === String(selectedId))
}

/** Sum an accessor over a list (1 per row by default). */
export const sumBy = (list, of) => (list ?? []).reduce((acc, r) => acc + (of(r) ?? 1), 0)

const INVARIANT_PLURALS = new Set(['Staff'])

/** `${n} ${word}` pluralized, honouring words that don't take an -s ("Staff"). */
export const pluralize = (n, word) =>
  `${n} ${word}${n === 1 || INVARIANT_PLURALS.has(word) ? '' : 's'}`
